import { ToolBase, ToolPointer } from './tool';
import { Vec3, closestPointOnSegment, projectPointOnPlane } from '../core/vec';
import { offsetFace } from '../core/ops';
import { faceLoopPoints, faceBasis, toBasis2D, pointInPolygon } from '../core/face';
import { formatLength, parseLength } from '../core/units';

/** Versatz (F): geschlossene Form parallel nach innen versetzen. */
export class OffsetTool extends ToolBase {
  readonly id = 'offset';
  readonly label = 'Versatz';
  readonly shortcut = 'F';
  readonly cursor = 'crosshair';
  readonly hint = 'Fläche anklicken und nach innen ziehen · Zahl tippen = Versatz';

  private faceId: number | null = null;
  private loop: Vec3[] = [];
  private dragging = false;
  private distance = 0;
  private previewPts: Vec3[] | null = null;

  enter(): void {
    this.faceId = null;
    this.dragging = false;
    this.previewPts = null;
  }

  exit(): void {
    this.ctx.renderer.hoverFaceId = null;
    this.ctx.preview.clear();
    this.ctx.requestUpdate();
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    if (this.dragging) return;
    // Ausgewählte Fläche bevorzugt, sonst Fläche unter dem Cursor
    let faceId = this.ctx.selection.faces.size === 1 ? [...this.ctx.selection.faces][0]! : null;
    if (faceId === null || !this.ctx.model.faces.has(faceId)) {
      faceId = this.ctx.renderer.pickEditableFace(e.ndcX, e.ndcY, this.ctx.camera.camera);
    }
    const face = faceId !== null ? this.ctx.model.faces.get(faceId) : null;
    if (!face || face.holes.length > 0) return;
    this.faceId = faceId;
    this.loop = faceLoopPoints(this.ctx.model, face);
    this.dragging = true;
    this.distance = 0;
    this.ctx.ui.setStatus('Nach innen ziehen · Zahl tippen = Versatz');
  }

  pointerMove(e: ToolPointer): void {
    this.ctx.preview.clear();
    if (!this.dragging) {
      const hit = this.ctx.renderer.pickEditableFace(e.ndcX, e.ndcY, this.ctx.camera.camera);
      this.ctx.renderer.hoverFaceId = hit;
      this.ctx.requestUpdate();
      return;
    }
    // Versatz = Abstand des Cursorpunkts (auf der Ebene der gegriffenen Fläche) zur nächsten
    // Kante ihres Rings — immer gegen die gegriffene Fläche, nicht gegen die unter dem Cursor
    const basis = faceBasis(this.loop);
    const onPlane = projectPointOnPlane(e.ray.origin, e.ray.dir, basis.origin, basis.n);
    if (!onPlane) return;
    const q = toBasis2D(basis, onPlane);
    const inside = pointInPolygon(q.x, q.y, this.loop.map((p) => toBasis2D(basis, p)));
    let minD = Infinity;
    for (let i = 0; i < this.loop.length; i++) {
      const d = closestPointOnSegment(onPlane, this.loop[i]!, this.loop[(i + 1) % this.loop.length]!).distance;
      minD = Math.min(minD, d);
    }
    if (!inside) minD = 0;
    this.distance = Number.isFinite(minD) ? minD : 0;
    this.drawPreview();
    this.ctx.ui.setStatus(`Versatz: ${formatLength(this.distance, this.ctx.units())} · Zahl tippen = Versatz`);
    this.ctx.requestUpdate();
  }

  private drawPreview(): void {
    // Vorschau: verkleinerten Loop grob Richtung Schwerpunkt ziehen
    if (!this.faceId) return;
    const face = this.ctx.model.faces.get(this.faceId);
    if (!face || this.distance <= 0) return;
    const cx = this.loop.reduce((s, p) => s + p.x, 0) / this.loop.length;
    const cy = this.loop.reduce((s, p) => s + p.y, 0) / this.loop.length;
    const cz = this.loop.reduce((s, p) => s + p.z, 0) / this.loop.length;
    // Peripherie-Radius bestimmen
    const perim = this.loop.reduce((s, p) => s + Math.hypot(p.x - cx, p.y - cy, p.z - cz), 0) / this.loop.length;
    const t = Math.max(0, 1 - this.distance / Math.max(perim, 1e-6));
    this.previewPts = this.loop.map((p) => ({
      x: cx + (p.x - cx) * t,
      y: cy + (p.y - cy) * t,
      z: cz + (p.z - cz) * t
    }));
    this.ctx.preview.polyline(this.previewPts, 0x333333, true);
  }

  pointerUp(_e: ToolPointer): void {
    if (!this.dragging) return;
    this.finish();
  }

  private finish(): void {
    if (this.faceId !== null && this.distance > 1e-6) {
      // Undo-Schritt erst bei echter Änderung
      this.ctx.beginEdit();
      offsetFace(this.ctx.model, this.faceId, this.distance);
    }
    this.dragging = false;
    this.faceId = null;
    this.previewPts = null;
    this.ctx.preview.clear();
    this.ctx.renderer.hoverFaceId = null;
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }

  wantsVcb(): boolean {
    return this.dragging;
  }

  vcbDisplay(): string | null {
    if (!this.dragging) return null;
    return formatLength(this.distance, this.ctx.units());
  }

  applyVcb(raw: string): void {
    if (!this.dragging) return;
    const d = parseLength(raw, this.ctx.units());
    if (d === null || d <= 0) return;
    this.distance = d;
    this.finish();
  }

  cancel(): void {
    this.dragging = false;
    this.faceId = null;
    this.previewPts = null;
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }
}
